import { Overlay, type OverlayRef, type ScrollStrategy } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { DOCUMENT, inject, Injectable, Injector, type Type } from '@angular/core';

import { CLARITY_FLY_IN_RESIZE } from '../constants/clarity-fly-in-resize.constant';
import type { ClarityFlyInConfig } from '../models/clarity-fly-in-config.model';
import type { ClarityFlyInEscape } from '../models/clarity-fly-in-escape.model';
import type { ClarityFlyInFocus } from '../models/clarity-fly-in-focus.model';
import type { ClarityFlyInStacking } from '../models/clarity-fly-in-stacking.model';
import type { ClarityFlyInTitleWeight } from '../models/clarity-fly-in-title-weight.model';
import type { ClarityFlyInWidth } from '../models/clarity-fly-in-width.model';
import { CLARITY_OVERLAY_DATA_TOKEN } from '../tokens/clarity-overlay-data.token';
import { ClarityFlyInContainerComponent } from './clarity-fly-in-container.component';
import { CLARITY_FLY_IN_PANEL_CLASS } from './clarity-fly-in-panel-class.constant';
import { ClarityFlyInRef } from './clarity-fly-in-ref';
import { ClarityStackedScrollLock } from './clarity-stacked-scroll-lock';

/** A fly-in is modal unless the caller says otherwise. */
const DEFAULT_MODAL = true;

/** A fly-in is a track beside the page unless the caller says otherwise. */
const DEFAULT_WIDTH: ClarityFlyInWidth = 'md';

/** A fly-in's body region draws its own inset and fill unless the caller says otherwise. */
const DEFAULT_BODY_INSET = true;
const DEFAULT_TITLE_WEIGHT: ClarityFlyInTitleWeight = 'default';

/**
 * The pane class each width adds beside `CLARITY_FLY_IN_PANEL_CLASS`. The default width adds none, so
 * the base pane rule alone states it and a panel opened before `width` existed renders unchanged.
 */
const WIDTH_PANEL_CLASS: Readonly<Record<ClarityFlyInWidth, string | undefined>> = {
	sm: `${CLARITY_FLY_IN_PANEL_CLASS}--sm`,
	md: undefined,
	lg: `${CLARITY_FLY_IN_PANEL_CLASS}--lg`,
	xl: `${CLARITY_FLY_IN_PANEL_CLASS}--xl`,
};

const RESIZE_MIN_WIDTH: Readonly<Record<ClarityFlyInWidth, number>> = {
	sm: CLARITY_FLY_IN_RESIZE.MIN_WIDTH,
	md: CLARITY_FLY_IN_RESIZE.MIN_WIDTH,
	lg: CLARITY_FLY_IN_RESIZE.MIN_WIDTH,
	xl: CLARITY_FLY_IN_RESIZE.WIDE_MIN_WIDTH,
};

/** A fly-in holds the one exclusive slot unless the caller says otherwise. */
const DEFAULT_STACKING: ClarityFlyInStacking = 'exclusive';

/**
 * The Escape and focus behaviour each stacking mode starts from. `exclusive` is the original
 * contract; `stacked` is brain's `SidePanel`, so a ported drawer needs to say nothing else.
 */
const DEFAULT_ESCAPE: Readonly<Record<ClarityFlyInStacking, ClarityFlyInEscape>> = {
	exclusive: 'top-most',
	stacked: 'document',
};
const DEFAULT_FOCUS: Readonly<Record<ClarityFlyInStacking, ClarityFlyInFocus>> = {
	exclusive: 'trap',
	stacked: 'first-focusable',
};

/**
 * One open request, resolved. Internal to this service — it is a parameter bundle, not a shape any
 * caller states, so it is deliberately absent from the barrel.
 */
interface ClarityFlyInRequest {
	readonly content: Type<unknown>;
	readonly config: ClarityFlyInConfig | undefined;
	readonly modal: boolean;
	readonly focus: ClarityFlyInFocus;
	readonly width: ClarityFlyInWidth;
}

/** Ported from Clarity `apps/web/src/shared/ui/SidePanel.tsx` (commit `f9217bb5a`). */
/**
 * Opens a component in an inline-end-anchored panel over the page.
 *
 * **One exclusive panel at a time.** Opening an `exclusive` panel (the default) disposes the exclusive
 * panel that is up and lets its `closed` emit before the new overlay is created, so there is never a
 * second exclusive pane in the DOM and never a ref whose caller is left believing its panel is still
 * up. Every consumer outside the System Domain console relies on that.
 *
 * **Stacked panels share the screen, as brain's do.** A `stacked` panel opens beside whatever is open
 * and closes nothing, and an exclusive open does not close it: brain's `SidePanel` never closes
 * another surface (decision 23 §1), so a console fly-in and the approvals / ACP panels stay open
 * together, each its own overlay, the later mount on top. Its caller owns its lifetime.
 *
 * **`modal` turns on three things together.** A modal fly-in gets a backdrop, `aria-modal` and a
 * page scroll lock; a non-modal one gets none of the three, so the page behind keeps scrolling and a
 * queue-status notice can sit beside the panel. They move as one because they are one claim — "the
 * page behind this is inert" — expressed to the pointer, to assistive technology and to the scroll
 * container. A stacked modal panel takes the lock through a shared counter, so it holds until the
 * last stacked panel closes.
 *
 * It provides no overlay container of its own: the app's own `CDK_OVERLAY_CONTAINER_PROVIDER`
 * decides where panes mount, and a second container would fragment the CDK's stacking order.
 */
@Injectable({ providedIn: 'root' })
export class ClarityFlyIn {
	private readonly overlay = inject(Overlay);
	private readonly injector = inject(Injector);
	private readonly document = inject(DOCUMENT);

	/**
	 * The exclusive panel that is open, held for one purpose: so the next exclusive `open` can close it
	 * first. A stacked panel is never held here.
	 *
	 * Typed by that one member rather than as `ClarityFlyInRef<unknown>`, which does not accept the ref
	 * `open` just built. A ref is invariant in `R` — its replay subject carries `R | undefined` and its
	 * `closed` observable hands it back out — so `ClarityFlyInRef<R>` is assignable to no other
	 * instantiation. Widening the field to the result type it never reads is the honest shape.
	 */
	private openRef: Pick<ClarityFlyInRef, 'close'> | undefined;

	/** Created on the first stacked modal panel; see `ClarityStackedScrollLock`. */
	private stackedScrollLock: ClarityStackedScrollLock | undefined;

	/**
	 * Opens `content` as a fly-in panel and returns the handle to it.
	 *
	 * `R` is what the body closes with; `D` is the payload it reads from
	 * `CLARITY_OVERLAY_DATA_TOKEN`.
	 */
	public open<R = unknown, D = unknown>(content: Type<unknown>, config?: ClarityFlyInConfig<D>): ClarityFlyInRef<R> {
		const stacking = config?.stacking ?? DEFAULT_STACKING;
		const stacked = stacking === 'stacked';

		if (!stacked) {
			this.openRef?.close();
		}

		const modal = config?.modal ?? DEFAULT_MODAL;
		const focus = config?.focus ?? DEFAULT_FOCUS[stacking];
		const width = config?.width ?? DEFAULT_WIDTH;
		const overlayRef = this.createOverlay(modal, stacked, width);
		const flyInRef = new ClarityFlyInRef<R>(overlayRef, config?.dismissible, {
			escape: config?.escape ?? DEFAULT_ESCAPE[stacking],
			focus,
			document: this.document,
		});

		this.attachContainer(overlayRef, flyInRef, { content, config, modal, focus, width });
		if (!stacked) {
			this.openRef = flyInRef;
		}

		return flyInRef;
	}

	private createOverlay(modal: boolean, stacked: boolean, width: ClarityFlyInWidth): OverlayRef {
		const widthClass = WIDTH_PANEL_CLASS[width];

		return this.overlay.create({
			// A global strategy rather than a connected one: the panel is anchored to the viewport's
			positionStrategy: this.overlay.position().global().end().top(),
			scrollStrategy: this.createScrollStrategy(modal, stacked),
			hasBackdrop: modal,
			panelClass:
				widthClass === undefined ? CLARITY_FLY_IN_PANEL_CLASS : [CLARITY_FLY_IN_PANEL_CLASS, widthClass],
		});
	}

	private createScrollStrategy(modal: boolean, stacked: boolean): ScrollStrategy {
		if (!modal) {
			return this.overlay.scrollStrategies.noop();
		}
		if (!stacked) {
			return this.overlay.scrollStrategies.block();
		}

		this.stackedScrollLock ??= new ClarityStackedScrollLock(this.overlay.scrollStrategies.block());

		return this.stackedScrollLock.strategy();
	}

	private attachContainer<R>(
		overlayRef: OverlayRef,
		flyInRef: ClarityFlyInRef<R>,
		request: ClarityFlyInRequest,
	): void {
		const contentInjector = Injector.create({
			parent: request.config?.injector ?? this.injector,
			providers: [
				{ provide: ClarityFlyInRef, useValue: flyInRef },
				{ provide: CLARITY_OVERLAY_DATA_TOKEN, useValue: request.config?.data },
			],
		});

		const containerRef = overlayRef.attach(
			new ComponentPortal(ClarityFlyInContainerComponent, null, contentInjector),
		);

		containerRef.setInput('content', request.content);
		containerRef.setInput('contentInjector', contentInjector);
		containerRef.setInput('modal', request.modal);
		containerRef.setInput('focus', request.focus);
		containerRef.setInput('title', request.config?.title);
		containerRef.setInput('titleWeight', request.config?.titleWeight ?? DEFAULT_TITLE_WEIGHT);
		containerRef.setInput('subtitle', request.config?.subtitle);
		containerRef.setInput('footer', request.config?.footer);
		containerRef.setInput('ariaLabel', request.config?.ariaLabel);
		containerRef.setInput('countBadge', request.config?.countBadge);
		containerRef.setInput('closeLabel', request.config?.closeLabel);
		containerRef.setInput('bodyInset', request.config?.bodyInset ?? DEFAULT_BODY_INSET);
		containerRef.setInput('minResizeWidth', RESIZE_MIN_WIDTH[request.width]);

		containerRef.instance.closeRequested.subscribe(() => {
			if (!flyInRef.headerCloseDisabled()) {
				flyInRef.requestClose();
			}
		});
	}
}
